import { medimoQC } from "@medimo/core-api/internal/next";
import { useQuery } from "@tanstack/react-query";
import { usePathname } from "next/navigation";
import type React from "react";
import { type ReactNode, createContext, useContext, useMemo } from "react";

interface SubscriptionActiveContextType {
  isActive: boolean;
  isPending: boolean;
}

const SubscriptionActiveContext = createContext<
  SubscriptionActiveContextType | undefined
>(undefined);

export const SubscriptionActiveProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const pathname = usePathname();
  const isAuthPage = pathname?.startsWith("/auth");

  const { error, isPending } = useQuery({
    ...medimoQC.queryOptions("get", "/v2/subscription/active"),
    retry: 0,
    enabled: !isAuthPage, // 認証ページではクエリを無効化
  });

  const isActive = useMemo(() => {
    if (process.env.NODE_ENV === "development") {
      return true; // ローカル開発環境では常に true
    }
    // @ts-ignore
    return error?.detail !== "The subscription does not exist.";
  }, [error]);

  return (
    <SubscriptionActiveContext.Provider value={{ isActive, isPending }}>
      {children}
    </SubscriptionActiveContext.Provider>
  );
};

export const useSubscriptionActive = (): SubscriptionActiveContextType => {
  const context = useContext(SubscriptionActiveContext);
  if (!context) {
    throw new Error(
      "useSubscriptionActive must be used within a SubscriptionActiveProvider",
    );
  }
  return context;
};

export const useIsSubscriptionActive = (): boolean => {
  const { isActive, isPending } = useSubscriptionActive();
  return isActive && !isPending;
};
